import React, { useCallback } from 'react';
import { useFocusEffect } from 'expo-router';
import { defineMessages, useIntl } from 'react-intl';

import { Modal } from '@garnish/overlays';
import { useSelector } from '@xstate/react';
import { type SnapshotFrom } from 'xstate';

import { GroupOrderConfirmationContent } from '@order/features-group-ordering';

import { useGlobalAppState } from '../../../../context/GlobalAppState';
import { useLeaveGroupOrderAndContinue } from '../../hooks/useLeaveGroupOrderAndContinue';

import type { reorderMachine } from '../../machines/Reorder.machine';

// ─────────────────────────────────────────────────────────────────────────────

export const GroupOrderLeaveConfirmationModal = (props: Props) => {
  const { isInsideModal } = props;

  const { formatMessage } = useIntl();
  const { reorderMachineRef } = useGlobalAppState();
  const isVisible = useSelector(reorderMachineRef, checkIfConfirmingGroupOrderLeave);
  const groupOrderReorderRequest = useSelector(reorderMachineRef, selectGroupOrderReorderRequest);
  const { isLeavingGroupOrder, leaveGroupOrderAndContinue } = useLeaveGroupOrderAndContinue({
    isInsideModal,
  });

  // ─── Handlers ──────────────────────────────────────────────────────────────

  const handleStay = useCallback(() => {
    if (isLeavingGroupOrder) {
      return;
    }

    reorderMachineRef.send({ type: 'CANCEL_GROUP_ORDER_LEAVE_CONFIRMATION' });
  }, [isLeavingGroupOrder, reorderMachineRef]);

  const handleLeave = useCallback(() => {
    if (!groupOrderReorderRequest) {
      return;
    }

    void leaveGroupOrderAndContinue(groupOrderReorderRequest.event);
  }, [groupOrderReorderRequest, leaveGroupOrderAndContinue]);

  const handleScreenFocus = useCallback(() => {
    return () => {
      const isConfirmingGroupOrderLeave = checkIfConfirmingGroupOrderLeave(
        reorderMachineRef.getSnapshot(),
      );

      if (!isConfirmingGroupOrderLeave) {
        return;
      }

      reorderMachineRef.send({ type: 'CANCEL_GROUP_ORDER_LEAVE_CONFIRMATION' });
    };
  }, [reorderMachineRef]);

  // ─── Effects ───────────────────────────────────────────────────────────────

  useFocusEffect(handleScreenFocus);

  // ───────────────────────────────────────────────────────────────────────────

  return (
    <Modal
      visible={isVisible}
      size="small"
      withSafeAreaView={false}
      accessibilityLabel={formatMessage(messages.accessibilityLabel)}
      onRequestClose={handleStay}
    >
      <GroupOrderConfirmationContent
        title={formatMessage(messages.title)}
        body={formatMessage(messages.body)}
        dismissButtonLabel={formatMessage(messages.stayCta)}
        confirmButtonLabel={formatMessage(messages.leaveCta)}
        confirmButtonPalette="caution"
        isPerformingAction={isLeavingGroupOrder}
        onClose={handleStay}
        onConfirm={handleLeave}
      />
    </Modal>
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function checkIfConfirmingGroupOrderLeave(snapshot: ReorderSnapshot) {
  return snapshot.matches('confirmingGroupOrderLeave');
}

function selectGroupOrderReorderRequest(snapshot: ReorderSnapshot) {
  return snapshot.context.groupOrderReorderRequest;
}

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  accessibilityLabel: {
    defaultMessage: 'Active group order',
    description: 'Reorder | Group order leave modal | Accessibility label',
  },
  title: {
    defaultMessage: 'You’re in a group order',
    description: 'Reorder | Group order leave modal | Title',
  },
  body: {
    defaultMessage:
      'Only the host can change the location. To order from a different location, leave the group order. Items you added will be removed.',
    description: 'Reorder | Group order leave modal | Body',
  },
  stayCta: {
    defaultMessage: 'Stay in group order',
    description: 'Reorder | Group order leave modal | Stay CTA',
  },
  leaveCta: {
    defaultMessage: 'Leave and continue',
    description: 'Reorder | Group order leave modal | Leave CTA',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  isInsideModal?: boolean;
};

type ReorderSnapshot = SnapshotFrom<typeof reorderMachine>;
